import React, { useState, useRef, useEffect } from 'react';
import { useProject } from '../context/ProjectContext';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import Skeleton from './Skeleton';

const WorkspaceSelectorSkeleton = () => (
  <div className="flex items-center gap-3 px-4 py-2 rounded-2xl bg-white/5 border border-white/10 min-w-[200px]">
    <Skeleton width="32px" height="32px" borderRadius="0.5rem" />
    <div className="flex-1 space-y-2">
      <Skeleton width="60px" height="8px" />
      <Skeleton width="100px" height="14px" />
    </div>
    <Skeleton width="16px" height="16px" borderRadius="4px" />
  </div>
);

const WorkspaceSelector = () => {
  const { workspaces, activeWorkspace, setActiveWorkspaceId, loading } = useProject();
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (loading) return <WorkspaceSelectorSkeleton />;
  if (workspaces.length === 0) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-4 py-2 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-all outline-none group min-w-[200px]"
      >
        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
          <Building2 size={18} />
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 leading-none mb-1">
            {t('activeWorkspace')}
          </div>
          <div className="text-sm font-bold text-slate-200 truncate pr-2">
            {activeWorkspace?.name || t('selectWorkspace')}
          </div>
        </div>
        <ChevronDown 
          size={16} 
          className={`text-slate-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 4, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute left-0 mt-2 w-72 bg-[#0f172a] border border-white/10 shadow-2xl z-50 overflow-hidden rounded-2xl p-3"
          >
            <div className="max-h-64 overflow-y-auto custom-scrollbar p-1 space-y-1">
              {workspaces.map((workspace) => (
                <button
                  key={workspace._id}
                  onClick={() => {
                    setActiveWorkspaceId(workspace._id);
                    // Clear projectId from URL so context picks a new one for this workspace
                    const params = new URLSearchParams(location.search);
                    params.delete('projectId');
                    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 p-3 rounded-2xl transition-all relative ${
                    activeWorkspace?._id === workspace._id ? 'text-primary' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'
                  }`}
                >
                  {activeWorkspace?._id === workspace._id && (
                    <motion.div
                      layoutId="active-workspace-indicator"
                      className="absolute inset-0 bg-primary/10 border border-primary/20 rounded-2xl"
                      initial={false}
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center relative z-10 ${
                    activeWorkspace?._id === workspace._id ? 'bg-primary text-white' : 'bg-white/5'
                  }`}>
                    <Building2 size={16} />
                  </div>
                  <span className="flex-1 text-left font-bold text-sm truncate relative z-10">{workspace.name}</span>
                  {activeWorkspace?._id === workspace._id && <Check size={16} className="relative z-10" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WorkspaceSelector;
